/**
 * @file: ProductReferralHistoryPage.tsx
 *
 * Rewards-history screen host. The nav-header title is replaced by a
 * segment control switching between two isolated tab-content components:
 * product-commission records and campaign-reward records. Reached in two
 * modes via `isServiceAgentShare` — own rewards vs service-advisor share.
 */

import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { SegmentControl } from '@euler/components/SegmentControl';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { CampaignRewardHistoryList } from './reward-history/CampaignRewardHistoryList';
import { ProductRewardHistoryList } from './reward-history/ProductRewardHistoryList';

export const ProductReferralHistoryScreen = wrapNavigatorScreen(
  ({ isServiceAgentShare }: { isServiceAgentShare?: boolean }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();
    const [selectedIndex, setSelectedIndex] = useState(0);

    const segments = useMemo(
      () =>
        isServiceAgentShare
          ? ['产品奖励分成', '活动奖励分成']
          : ['产品奖励', '活动奖励'],
      [isServiceAgentShare],
    );

    useEffect(() => {
      navigation.setOptions({
        headerTitle: () => (
          <SegmentControl
            segments={segments}
            selectedIndex={selectedIndex}
            onSelectedIndexChange={setSelectedIndex}
            style={{
              width: rpx(240),
              height: rpx(32),
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              borderRadius: rpx(16),
            }}
            sliderStyle={{
              backgroundColor: '#fff',
              borderRadius: rpx(16),
            }}
            textStyle={{
              color: '#fff',
              fontSize: rpx(13),
              fontFamily: FontFamily.NotoSans.Medium,
            }}
            activeTextStyle={{
              color: '#2669db',
              fontSize: rpx(13),
              fontFamily: FontFamily.NotoSans.Bold,
            }}
          />
        ),
      });
      // segments is derived from isServiceAgentShare; both are inputs.
    }, [navigation, selectedIndex, isServiceAgentShare, rpx, segments]);

    return (
      <View style={{ flex: 1 }}>
        {selectedIndex === 0 ? (
          <ProductRewardHistoryList isServiceAgentShare={isServiceAgentShare} />
        ) : (
          <CampaignRewardHistoryList isShare={isServiceAgentShare} />
        )}
      </View>
    );
  },
  {
    title: '奖励记录',
  },
);
